interface DependsOnProperty {
    name: string
    rules: DependsOnRule[]
    and?: DependsOnProperty[]
    or?: DependsOnProperty[]
}

interface DependsOnRule {
    value: any
    condition?: string
}

export class RuleEngine {
    private conditionShortNameMapping: { [key: string]: string };
    [key: string]: any

    constructor() {
        this.conditionShortNameMapping = {
            "===": "isEqual",
            "!==": "isNotEqual",
            "<": "isLessThan",
            ">": "isGreaterThan",
            "<=": "isLessThanOrEqualTo",
            ">=": "isGreaterThanOrEqualTo",
        }
    }

    public resolve(dependsOn: DependsOnProperty[], values: any): boolean {
        return this.resolveAndGroup(dependsOn, values)
    }

    public resolveAndGroup(dependsOn: DependsOnProperty[], values: any): boolean {
        if (dependsOn && dependsOn.length) {
            for (let i = 0; i < dependsOn.length; i++) {
                if (dependsOn[i].hasOwnProperty("and")) {
                    if (!this.resolveAndGroup(dependsOn[i]["and"], values)) {
                        return false
                    }
                } else if (dependsOn[i].hasOwnProperty("or")) {
                    if (!this.resolveOrGroup(dependsOn[i]["or"], values)) {
                        return false
                    }
                } else {
                    let value = this.getValueByFieldName(dependsOn[i].name, values)
                    if (!this.resolveRule(dependsOn[i], value)) {
                        return false
                    }
                }
            }
        }

        return true
    }

    public resolveOrGroup(dependsOn: DependsOnProperty[], values: any): boolean {
        if (dependsOn && dependsOn.length) {
            for (let i = 0; i < dependsOn.length; i++) {
                if (dependsOn[i].hasOwnProperty("and")) {
                    if (this.resolveAndGroup(dependsOn[i]["and"], values)) {
                        return true
                    }
                } else if (dependsOn[i].hasOwnProperty("or")) {
                    if (this.resolveOrGroup(dependsOn[i]["or"], values)) {
                        return true
                    }
                } else {
                    let value = this.getValueByFieldName(dependsOn[i].name, values)
                    if (this.resolveRule(dependsOn[i], value)) {
                        return true
                    }
                }
            }
        }

        return false
    }

    private getValueByFieldName(key: string, object: Record<string, any>): any {
        let value

        Object.keys(object).some((k) => {
            if (k === key) {
                value = object[k]
                return true
            }
            if (object[k] && typeof object[k] === "object") {
                value = this.getValueByFieldName(key, object[k])
                return value !== undefined
            }
        })

        return value
    }

    private resolveRule(dependsOn: DependsOnProperty, value: any): boolean {
        for (let i = 0; i < dependsOn.rules.length; i++) {
            let rule = dependsOn.rules[i]

            let methodName = this.conditionShortNameMapping[rule.condition]
                ? this.conditionShortNameMapping[rule.condition]
                : rule.condition

            if (rule.condition && typeof this[methodName] === "function") {
                if (!this[rule.condition](rule, value)) {
                    return false
                }
            } else {
                if (!this.isEqual(rule, value)) {
                    return false
                }
            }
        }

        return true
    }

    private isEmpty(rule: DependsOnRule, value: any): boolean {
        if (Array.isArray(value)) {
            return value.length === 0
        }

        return !value
    }

    private isNotEmpty(rule: DependsOnRule, value: any): boolean {
        if (Array.isArray(value)) {
            return value.length > 0
        }

        return value
    }

    private isEqual(rule: DependsOnRule, value: any): boolean {
        return rule.value === value
    }

    private isNotEqual(rule: DependsOnRule, value: any): boolean {
        return rule.value !== value
    }

    private isLessThan(rule: DependsOnRule, value: any): boolean {
        return value < rule.value
    }

    private isGreaterThan(rule: DependsOnRule, value: any): boolean {
        return value > rule.value
    }

    private isLessThanOrEqualTo(rule: DependsOnRule, value: any): boolean {
        return value <= rule.value
    }

    private isGreaterThanOrEqualTo(rule: DependsOnRule, value: any): boolean {
        return value >= rule.value
    }

    private isIn(rule: DependsOnRule, value: any): boolean {
        return value && value.includes(rule.value)
    }

    private isNotIn(rule: DependsOnRule, value: any): boolean {
        return value && !value.includes(rule.value)
    }
}
